/**
 * PixiJS v8 application wrapper. Owns the overlay canvas, scales it to the
 * reference 1792×828 logical coordinate space, and exposes a root Container
 * for the ChipLayer (and any future Pixi-rendered UI) to live in.
 */
import { Application, Container } from 'pixi.js';
import { REF_HEIGHT, REF_WIDTH } from '../config';

export class PixiLayer {
  readonly app: Application;
  readonly root: Container;
  private readonly canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.app = new Application();
    this.root = new Container();
    this.root.label = 'Root';
  }

  async init(): Promise<void> {
    await this.app.init({
      canvas: this.canvas,
      width: REF_WIDTH,
      height: REF_HEIGHT,
      background: 0x000000,
      backgroundAlpha: 0,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      preference: 'webgl',
    });
    this.app.stage.addChild(this.root);
    this.fit();
    window.addEventListener('resize', () => this.fit());
  }

  /**
   * The CSS layer is scaled by ResponsiveScaler, but Pixi's internal canvas
   * size must stay at logical 1792×828 so coordinates inside `root` are
   * predictable.
   */
  private fit(): void {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    this.app.renderer.resolution = Math.min(window.devicePixelRatio || 1, 2);
    // Keep logical size constant.
    this.app.renderer.resize(REF_WIDTH, REF_HEIGHT);
  }
}
